import { useState, useEffect } from 'react';
import { getStockAnalysis, getDemoTier } from '../lib/cerebro';
import { ANALYSIS_BANK } from '../lib/analysisBank';

// Stock metadata for the picker
const STOCK_META = {
  AAPL: { name: 'Apple', emoji: '🍎' },
  MSFT: { name: 'Microsoft', emoji: '💼' },
  NVDA: { name: 'NVIDIA', emoji: '🔌' },
  AMD: { name: 'AMD', emoji: '⚙️' },
  TSLA: { name: 'Tesla', emoji: '🚗' },
  PLTR: { name: 'Palantir', emoji: '🛡️' },
  HIMS: { name: 'Hims & Hers', emoji: '💊' },
  DUOL: { name: 'Duolingo', emoji: '🦉' },
  VOO: { name: 'S&P 500', emoji: '🏛️' },
  QQQ: { name: 'Nasdaq 100', emoji: '📈' },
  BND: { name: 'Bonos US', emoji: '🛡️' },
  GLD: { name: 'Oro', emoji: '🥇' },
  SCHD: { name: 'Dividendos', emoji: '💰' },
  SOXX: { name: 'Semis', emoji: '🔬' },
  ARKK: { name: 'Innovación', emoji: '🚀' },
  VWO: { name: 'Emergentes', emoji: '🌎' },
  // LATAM + Mega-caps adicionales
  NU: { name: 'Nubank', emoji: '💜' },
  MELI: { name: 'MercadoLibre', emoji: '🛒' },
  GOOGL: { name: 'Alphabet', emoji: '🔍' },
  AMZN: { name: 'Amazon', emoji: '📦' },
  META: { name: 'Meta', emoji: '🌐' },
  COIN: { name: 'Coinbase', emoji: '₿' },
  SHOP: { name: 'Shopify', emoji: '🛍️' },
  V: { name: 'Visa', emoji: '💳' },
  TSM: { name: 'Taiwan Semiconductor', emoji: '🏭' },
  BABA: { name: 'Alibaba', emoji: '🐉' },
  ORCL: { name: 'Oracle', emoji: '🗄️' },
  GLOB: { name: 'Globant', emoji: '💻' },
  SOFI: { name: 'SoFi', emoji: '🏦' },
};

/**
 * StockComparator — Side-by-side comparison of 2-3 stocks
 *
 * Shows key metrics, scenarios, and qualitative summaries
 * across multiple stocks for direct comparison.
 *
 * Pre-configured comparison sets by sector.
 */

const COMPARISON_SETS = [
  {
    id: 'ai-chips',
    title: '🔥 Batalla de Chips IA',
    desc: 'NVIDIA vs AMD — los dos gigantes de chips para inteligencia artificial',
    symbols: ['NVDA', 'AMD'],
  },
  {
    id: 'big-tech',
    title: '💼 Big Tech Empresarial',
    desc: 'Apple vs Microsoft — los dos gigantes más estables del mundo tech',
    symbols: ['AAPL', 'MSFT'],
  },
  {
    id: 'disruptors',
    title: '🚀 Disruptores',
    desc: 'Tesla vs Palantir — apuestas concentradas a transformación masiva',
    symbols: ['TSLA', 'PLTR'],
  },
  {
    id: 'growth-consumer',
    title: '📈 Growth Consumer',
    desc: 'HIMS vs DUOL — empresas de consumo con modelos de suscripción',
    symbols: ['HIMS', 'DUOL'],
  },
  {
    id: 'core-etfs',
    title: '🏛️ ETFs Core',
    desc: 'VOO vs QQQ — el mercado completo vs tech concentrado',
    symbols: ['VOO', 'QQQ'],
  },
  {
    id: 'defensive',
    title: '🛡️ Defensivos',
    desc: 'BND vs GLD vs SCHD — los tres activos defensivos clásicos',
    symbols: ['BND', 'GLD', 'SCHD'],
  },
  {
    id: 'latam',
    title: '🌎 Líderes LATAM',
    desc: 'Nubank vs MercadoLibre — los dos gigantes digitales de Latinoamérica',
    symbols: ['NU', 'MELI'],
  },
  {
    id: 'mega-caps',
    title: '👑 Mega-caps Tech',
    desc: 'Las cuatro mega-caps más valiosas: Apple, Microsoft, Alphabet y Amazon',
    symbols: ['AAPL', 'MSFT', 'GOOGL'],
  },
  {
    id: 'fintech',
    title: '💳 Fintech',
    desc: 'Visa vs Coinbase — los dos modelos opuestos de pagos: tradicional vs crypto',
    symbols: ['V', 'COIN'],
  },
];

export default function StockComparator() {
  const [selectedSet, setSelectedSet] = useState(COMPARISON_SETS[0]);
  const [customMode, setCustomMode] = useState(false);
  const [customSymbols, setCustomSymbols] = useState(['NVDA', 'AMD']);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeSymbols = customMode
    ? customSymbols.filter(Boolean)
    : selectedSet.symbols;
  const availableSymbols = Object.keys(ANALYSIS_BANK);

  useEffect(() => {
    loadComparison();
  }, [selectedSet, customMode, customSymbols]);

  async function loadComparison() {
    if (activeSymbols.length < 2) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const demoTier = getDemoTier() || 'premium';
      const results = await Promise.all(
        activeSymbols.map((s) => getStockAnalysis(s, demoTier))
      );
      setData(results);
    } catch (err) {
      console.error('[Comparator]', err.message);
    } finally {
      setLoading(false);
    }
  }

  const toggleCustomSymbol = (symbol) => {
    if (customSymbols.includes(symbol)) {
      // Remove if more than 2 selected
      if (customSymbols.length > 2) {
        setCustomSymbols(customSymbols.filter(s => s !== symbol));
      }
    } else {
      // Add up to 3
      if (customSymbols.length < 3) {
        setCustomSymbols([...customSymbols, symbol]);
      }
    }
  };

  const numCols = activeSymbols.length;
  const gridClass = numCols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3';

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">⚖️</span>
          <span className="text-base font-semibold text-[#181D27]">Comparador</span>
        </div>
        <p className="text-xs text-[#6B7280]">
          Compara dos o tres acciones lado a lado para ver cuál encaja mejor con tu tesis.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setCustomMode(false)}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
            !customMode
              ? 'bg-[#2A59C6] text-white border-[#2A59C6]'
              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#2A59C6]'
          }`}
        >
          🎯 Comparaciones sugeridas
        </button>
        <button
          onClick={() => setCustomMode(true)}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
            customMode
              ? 'bg-[#2A59C6] text-white border-[#2A59C6]'
              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#2A59C6]'
          }`}
        >
          ✨ Elige tú mismo
        </button>
      </div>

      {!customMode ? (
        <>
          {/* Selector de set sugerido */}
          <div className="flex flex-wrap gap-2 mb-4">
            {COMPARISON_SETS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedSet(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  selectedSet.id === s.id
                    ? 'bg-[#2A59C6] text-white border-[#2A59C6]'
                    : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#2A59C6]'
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>

          {/* Descripción del set actual */}
          <div className="text-xs text-[#4B5563] mb-4 bg-[#F0F4FF] rounded-lg p-3">
            {selectedSet.desc}
          </div>
        </>
      ) : (
        <>
          {/* Custom picker — 3 search boxes */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <StockSearchBox
              label="Acción 1"
              selected={customSymbols[0]}
              available={availableSymbols}
              onSelect={(s) => setCustomSymbols([s, customSymbols[1], customSymbols[2]].filter(Boolean))}
              onClear={() => setCustomSymbols(customSymbols.filter((_, i) => i !== 0))}
              required
            />
            <StockSearchBox
              label="Acción 2"
              selected={customSymbols[1]}
              available={availableSymbols.filter(s => s !== customSymbols[0] && s !== customSymbols[2])}
              onSelect={(s) => setCustomSymbols([customSymbols[0], s, customSymbols[2]].filter(Boolean))}
              onClear={() => setCustomSymbols(customSymbols.filter((_, i) => i !== 1))}
              required
            />
            <StockSearchBox
              label="Acción 3 (opcional)"
              selected={customSymbols[2]}
              available={availableSymbols.filter(s => s !== customSymbols[0] && s !== customSymbols[1])}
              onSelect={(s) => setCustomSymbols([customSymbols[0], customSymbols[1], s].filter(Boolean))}
              onClear={() => setCustomSymbols(customSymbols.filter((_, i) => i !== 2))}
            />
          </div>
          {customSymbols.filter(Boolean).length < 2 && (
            <div className="text-xs text-[#DC2626] mb-3 text-center bg-[#FEF2F2] rounded-lg p-2">
              Selecciona al menos 2 acciones para comparar.
            </div>
          )}
        </>
      )}

      {/* Comparación */}
      {loading ? (
        <div className="py-8 text-center text-xs text-[#9CA3AF]">Cargando comparación...</div>
      ) : (
        <div className={`grid ${gridClass} gap-3`}>
          {data.map((stock) => (
            <ComparisonCard key={stock.symbol} stock={stock} />
          ))}
        </div>
      )}
    </div>
  );
}

function ComparisonCard({ stock }) {
  if (!stock) return null;

  const f = stock.fundamentals || {};
  const s = stock.scenarios;

  return (
    <div className="border border-[#E5E7EB] rounded-xl p-3">
      {/* Header */}
      <div className="border-b border-[#F3F4F6] pb-2 mb-3">
        <div className="text-base font-bold text-[#181D27]">{stock.symbol}</div>
        <div className="text-[10px] text-[#9CA3AF]">{f.industry || f.sector || '—'}</div>
      </div>

      {/* Fundamentales clave */}
      <div className="space-y-1.5 mb-3">
        <Metric label="Tamaño" value={f.marketCap || '—'} />
        <Metric label="P/E" value={f.pe || '—'} />
        {f.dividendYield !== undefined && <Metric label="Dividendo" value={`${f.dividendYield}%`} />}
        {f.netMargin !== undefined && <Metric label="Margen Neto" value={`${f.netMargin}%`} />}
        {f.expenseRatio && <Metric label="Comisión" value={f.expenseRatio} />}
      </div>

      {/* Escenarios */}
      {s && (
        <div className="space-y-1.5 mb-3 border-t border-[#F3F4F6] pt-2">
          <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide">
            Escenarios {s.horizon}
          </div>
          <ScenarioMini emoji="🚀" label="Optimista" multiplier={s.bull.multiplier} color="#059669" />
          <ScenarioMini emoji="⚖️" label="Realista" multiplier={s.real.multiplier} color="#2A59C6" />
          <ScenarioMini emoji="⚠️" label="Pesimista" multiplier={s.bear.multiplier} color="#DC2626" />
        </div>
      )}

      {/* Resumen 1-liner */}
      {stock.qualitative?.whatTheySolve && (
        <div className="border-t border-[#F3F4F6] pt-2">
          <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
            Qué resuelve
          </div>
          <p className="text-[11px] text-[#4B5563] leading-relaxed line-clamp-3">
            {stock.qualitative.whatTheySolve.substring(0, 180)}
            {stock.qualitative.whatTheySolve.length > 180 ? '...' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-[#6B7280]">{label}</span>
      <span className="text-xs font-semibold text-[#181D27]">{value}</span>
    </div>
  );
}

function ScenarioMini({ emoji, label, multiplier, color }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <div className="flex items-center gap-1.5">
        <span>{emoji}</span>
        <span className="text-[#6B7280]">{label}</span>
      </div>
      <span className="font-bold" style={{ color }}>
        {multiplier}x
      </span>
    </div>
  );
}

function StockSearchBox({ label, selected, available, onSelect, onClear, required }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = available.filter((sym) => {
    if (!search) return true;
    const meta = STOCK_META[sym] || { name: sym, emoji: '📊' };
    const q = search.toLowerCase();
    return sym.toLowerCase().includes(q) || meta.name.toLowerCase().includes(q);
  });

  const selectedMeta = selected ? (STOCK_META[selected] || { name: selected, emoji: '📊' }) : null;

  return (
    <div className="relative">
      <div className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
        {label}
      </div>

      {/* Selected display or empty input */}
      {selected ? (
        <div className="flex items-center justify-between bg-[#2A59C6] text-white rounded-lg px-3 py-2.5 border-2 border-[#2A59C6]">
          <div className="flex items-center gap-2">
            <span className="text-base">{selectedMeta.emoji}</span>
            <div>
              <div className="text-sm font-bold">{selected}</div>
              <div className="text-[10px] opacity-90">{selectedMeta.name}</div>
            </div>
          </div>
          <button
            onClick={() => { onClear(); setSearch(''); }}
            className="text-white/80 hover:text-white"
            aria-label="Quitar"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      ) : (
        <>
          <div className={`relative border-2 rounded-lg transition ${open ? 'border-[#2A59C6]' : 'border-[#E5E7EB]'}`}>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder={required ? "Buscar (ej: Apple, AAPL)" : "Opcional"}
              className="w-full px-3 py-2.5 text-sm outline-none rounded-lg pr-9"
            />
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>

          {/* Dropdown */}
          {open && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-[#9CA3AF]">No hay resultados</div>
              ) : (
                filtered.map((sym) => {
                  const meta = STOCK_META[sym] || { name: sym, emoji: '📊' };
                  return (
                    <button
                      key={sym}
                      onMouseDown={(e) => { e.preventDefault(); onSelect(sym); setSearch(''); setOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#F0F4FF] flex items-center gap-2.5 border-b border-[#F9FAFB] last:border-0"
                    >
                      <span className="text-base">{meta.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#181D27]">{sym}</div>
                        <div className="text-[11px] text-[#6B7280] truncate">{meta.name}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
